import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Col,
  Form,
  Row,
  Tabs,
  Input,
  Button,
  Select,
  Switch,
  Radio,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import TitlePage from 'components/theme_components/Title';
import Title from 'components/Title';
import DF from 'DisplayFramework';
import DataGrid from 'components/theme_components/DataGrid';
import { PlusOutlined } from '@ant-design/icons';
import {
  addParRate,
  getParRateCode,
  type ParRateResponse,
  type ParRateValue,
  updateParRate,
} from 'services/parametersService';
import { AuthContext } from 'components/Auth/Auth';
import FormInput from 'components/FormInput';
import IPen from 'icons/IPen';
import ModalSFS from 'components/theme_components/ModalSFS';
import ParRateValueModal from './ParRateValueModal';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ButtonLink from 'components/theme_components/ButtonLink';
import Notifications from 'components/theme_components/Notifications';
import ParListSelect from 'components/ParListSelect';
import { toRate } from 'utils/utils';
import moment from 'moment';

const { TabPane } = Tabs;
export type ParRateDetailProps = {
  isCreate?: boolean;
};

const ParRateDetails = ({ isCreate }: ParRateDetailProps) => {
  const { id } = useParams<{
    id: string;
  }>();
  
  // État pour suivre si le taux vient d'être créé
  const [isJustCreated, setIsJustCreated] = useState(false);
  const [createdRateCode, setCreatedRateCode] = useState<string>('');
  
  const { data: values, refetch } = useQuery<ParRateResponse>(
    `getParRateCode/${id || createdRateCode}`,
    () => getParRateCode({ ratecode: (id || createdRateCode)! }).then((res: any) => res.data),
    {
      enabled: !!(id || createdRateCode),
    }
  );
  const navigate = useNavigate();
  const [form] = useForm();
  const fieldName = 'parRateValueLists';

  useEffect(() => {
    if (values) {
      form.setFieldsValue(values);
      form.setFieldsValue({
        ...values,
        ratestatus: values.ratestatus,
        parRateLists: values.parRateLists.map((valuerate: ParRateValue) => ({
          ...valuerate,
        })),
      });
    }
  }, [form, values]);

  // Set default values for create mode
  useEffect(() => {
    if (isCreate && !isJustCreated) {
      form.setFieldsValue({
        ratestatus: 'ACTIVE',
        ratecategory: 'MARGE',
        ratefrequency: 'MONTHLY',
        ratetype: 'NOMINAL',
      });
    }
  }, [isCreate, isJustCreated, form]);

  const Rates: ParRateValue[] = form.getFieldValue('parRateLists');
  console.log({ Rates }, form.getFieldValue(fieldName));
  const { isInspect } = useContext(AuthContext);

  // Watch form values for conditional rendering
  const ratetype = Form.useWatch('ratetype', form);
  const ratecode = Form.useWatch('ratecode', form);
  const ratecategory = Form.useWatch('ratecategory', form);

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return ratecode && ratetype && ratecategory;
  }, [ratecode, ratetype, ratecategory]);

  // Check if add rate value button should be enabled
  const canAddRateValue = useMemo(() => {
    if (!values?.parRateLists || values.parRateLists.length === 0) {
      return true;
    }
    return values.parRateLists.every((rate) => rate.enddate);
  }, [values?.parRateLists]);

  // Show rate values section - modifier la logique
  const showRateValuesSection = useMemo(() => {
    return !isCreate || isJustCreated; // Montrer en mode édition OU après création
  }, [isCreate, isJustCreated]);

  const columns = [
    {
      dataIndex: 'ratevalue',
      key: 'ratevalue',
      width: '120',
      render: (val: number) => toRate(val),
    },
    {
      dataIndex: 'begindate',
      key: 'begindate',
      width: '120',
      render: (val: string) => <div>{val?.substring(0, 10)}</div>,
    },
    {
      dataIndex: 'enddate',
      key: 'endate',
      width: '120',
      render: (val: string) => <div>{val?.substring(0, 10)}</div>,
    },
    {
      title: '',
      key: 'arrow',
      width: '50px',
      render: (record: ParRateResponse) => (
        <ModalSFS
          className="color-gray"
          trigger={({ setVisible }) => (
            <IPen
              onClick={() => setVisible(true)}
              style={{
                cursor: 'pointer',
              }}
              color={'#4B5672'}
            />
          )}
          title="updateratevalue"
          footer
          width={560}
        >
          <ParRateValueModal
            setVisible={() => false}
            data={record as any}
            refetch={() => {
              refetch();
            }}
            ratecode={record.ratecode}
          />
        </ModalSFS>
      ),
      onHeaderCell: () => ({
        style: { paddingLeft: '12px' },
      }),
    },
  ];

  const handleSubmit = () => {
    if (isCreate && !isJustCreated) {
      // Première création du taux
      const formRateCode = form.getFieldValue('ratecode');
      const request: any = {
        ratecode: formRateCode,
        ratecategory: form.getFieldValue('ratecategory') ?? 'MARGE',
        creationdate:
          form.getFieldValue('creationdate') ?? moment().format('YYYY-MM-DD'),
        ratedescription: form.getFieldValue('ratedescription'),
        ratestatus: 'ACTIVE',
        ratetype: form.getFieldValue('ratetype'),
        ceilingrate: form.getFieldValue('ceilingrate') ?? null,
        ceilingdefaultrate: form.getFieldValue('ceilingdefaultrate') ?? null,
        floorrate: form.getFieldValue('floorrate'),
        floordefaultrate: form.getFieldValue('floordefaultrate'),
        calid: form.getFieldValue('calid'),
      };
      
      addParRate({ request: request }).then((res) => {
        if (res.code === '200') {
          Notifications({
            status: 'success',
            message: 'Le taux a été créé avec succès',
            description: res.description,
            duration: 3,
            placement: 'top',
          });
          // Marquer comme créé et permettre l'ajout de valeurs
          setIsJustCreated(true);
          setCreatedRateCode(formRateCode);
          // Recharger les données
          refetch();
        } else {
          Notifications({
            status: 'error',
            message: res.message,
            description: res.description,
            duration: 3,
            placement: 'top',
          });
        }
      });
    } else if (isJustCreated) {
      // Deuxième clic : terminer et naviguer
      navigate('/configuration-sfs?tab=3');
    } else {
      // Mode édition normal
      const request: any = {
        ratecode: form.getFieldValue('ratecode'),
        ratecategory: form.getFieldValue('ratecategory') ?? 'MARGE',
        creationdate: form.getFieldValue('creationdate'),
        ratedescription: form.getFieldValue('ratedescription'),
        ratestatus: form.getFieldValue('ratestatus'),
        ratetype: form.getFieldValue('ratetype'),
        ceilingrate: form.getFieldValue('ceilingrate') ?? null,
        ceilingdefaultrate: form.getFieldValue('ceilingdefaultrate') ?? null,
        floorrate: form.getFieldValue('floorrate'),
        floordefaultrate: form.getFieldValue('floordefaultrate'),
        calid: form.getFieldValue('calid'),
      };
      
      updateParRate({ ratecode: String(id), request }).then((res) => {
        if (res.code === '200') {
          Notifications({
            status: 'success',
            message: res.message,
            description: res.description,
            duration: 3,
            placement: 'top',
          });
          refetch();
          navigate('/configuration-sfs?tab=3');
        } else {
          Notifications({
            status: 'error',
            message: res.message,
            description: res.description,
            duration: 3,
            placement: 'top',
          });
        }
      });
    }
  };

  // Texte du bouton dynamique
  const getSubmitButtonText = () => {
    if (isCreate && !isJustCreated) {
      return 'validate'; 
    } else if (isJustCreated) {
      return 'validate'; 
    } else {
      return 'Mettre à jour'; 
    }
  };

  return (
    <DF.Provider pagcode="productlist">
      <div className="bg-color-gray transferSFS">
        <TitlePage
          level={2}
          comcode="ParRateDetails"
          bgColor="purple"
          noTitleOverlay
        />

        <Form form={form} layout="vertical">
          <div className="container">
            <Row
              className="mb-4 ut-wizard-form"
              gutter={24}
              justify="center"
              align="top"
            >
              <Col span={20}>
                <Title
                  comcode="inforates"
                  level={4}
                  className="ut-updateTP-title mb-7 mt-10"
                />
              </Col>
              <Col span={20}>
                <FormInput name="ratetype" rules={[{ required: isCreate }]}>
                  <Radio.Group
                    defaultValue="NOMINAL"
                    style={{ width: '100%' }}
                    disabled={!isCreate || isJustCreated}
                  >
                    <Radio value="NOMINAL">
                      <GetLabel label="manual" />
                    </Radio>
                    <Radio value="FLOAT">
                      <GetLabel label="auto" />
                    </Radio>
                  </Radio.Group>
                </FormInput>
              </Col>

              <Col span={10}>
                <FormInput
                  name="ratecode"
                  rules={[
                    { required: true },
                    {
                      validator: async (_, value) => {
                        if (value && value.length > 10) {
                          return Promise.reject(
                            new Error('Le code de taux ne doit pas dépasser 10 caractères')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input disabled={!isCreate || isJustCreated} />
                </FormInput>
                <FormInput
                  name="ratecategory"
                  className="no-border-radius"
                  rules={[{ required: true }]}
                >
                  <ParListSelect parcode="SFSRATECAT" disabled={isJustCreated} />
                </FormInput>
              </Col>
              <Col span={10}>
                <FormInput
                  name="ratedescription"
                  className="no-border-radius"
                  labelCol={{ span: 24 }}
                >
                  <Input disabled={isJustCreated} />
                </FormInput>

                <FormInput name="ratestatus">
                  <Input disabled />
                </FormInput>
              </Col>

              {showRateValuesSection && (
                <>
                  <Col span={16} />
                  <Col
                    span={4}
                    className="d-flex justify-end align-center mb-4"
                  >
                    <ModalSFS
                      className="color-gray"
                      trigger={({ setVisible }) => (
                        <Button
                          onClick={() => !isInspect && setVisible(true)}
                          icon={<PlusOutlined />}
                          className={
                            ratetype === 'NOMINAL' && canAddRateValue
                              ? 'no-border-radius ant-btn-outline-primary ml-2 filter-item ant-btn-add bg-color-hover'
                              : 'no-border-radius filter-item'
                          }
                          disabled={!canAddRateValue}
                        >
                          <GetLabel label="addratevalue" />
                        </Button>
                      )}
                      title="addrate"
                      footer
                      width={560}
                    >
                      <ParRateValueModal
                        setVisible={() => false}
                        endDatePrevious={
                          values?.parRateLists?.length
                            ? values.parRateLists[0].enddate
                            : undefined
                        }
                        data={[] as any}
                        refetch={() => {
                          refetch();
                        }}
                        ratecode={createdRateCode || id}
                      />
                    </ModalSFS>
                  </Col>
                  <Col xs={20}>
                    <Form.Item noStyle shouldUpdate>
                      <DataGrid
                        columns={columns}
                        dataSource={values?.parRateLists}
                        rowKey="formulacode"
                        hideFilters
                        hideTitle
                        pagination={{
                          pageSize: 10,
                          showTotal: (total, range) =>
                            `${range[0]}-${range[1]} sur ${total}`,
                          showSizeChanger: true,
                        }}
                        className="no-border-radius mt-4"
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              <Col span={20}>
                <Row justify="end" className="mt-7">
                  <ButtonLink
                    comcode="Annuler"
                    className="ut-btn-bordred"
                    onClick={() => {
                      navigate('/configuration-sfs?tab=3');
                    }}
                  />
                  {(isCreate ? isFormValid : ratetype === 'NOMINAL') && (
                    <ButtonLink
                      type="primary"
                      comcode={getSubmitButtonText()}
                      htmlType="submit"
                      className="ut-reset-btn"
                      iconPosition="end"
                      onClick={() => handleSubmit()}
                    />
                  )}
                </Row>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </DF.Provider>
  );
};

export default ParRateDetails;